import React, { useCallback, useMemo, useState } from 'react';
import { Formik } from 'formik';
import { Button, Grid, LinearProgress } from '@material-ui/core';
import { useQuery } from '@apollo/client';
import { getOptions } from '@ourbranch/lookups';
import { affinityToStem } from '@ourbranch/policy-utils';

import { brandConfig } from 'brand-config';
import { Label } from 'core';
import Field from 'core/components/form/form.v2';
import ValueField from 'core/components/value-field';
import useSession from 'core/hooks/use-session';
import { useStore } from 'core/store';
import { Card } from 'core/components/card';
import { Tooltip } from 'core/components/tooltip';
import { currencyFormatter } from 'core/helpers/formatters';
import icon from 'core/assets/svg/alert-white-bold.svg';
import { LabelTooltip } from 'core/components/label-tooltip';
import { tooltipHoverTexts } from 'core/helpers/constants';
import { GET_POLICY_SWEEP_ACCOUNT, GET_SWEEP_ACCOUNT } from 'core/components/sweep-account/sweep-account.queries';
import { isSweepAccountsEnabled } from 'core/helpers/sweep-account';
import { isAgentPaymentOptionsEnabled } from 'core/helpers/agent-payment-options';
import { creditCardFeeApplies } from 'core/helpers/credit-card-fee';
import { paymentMethodFieldLoadingProps, remainingBalance } from './manual-charge-form.helpers';
import { resolvePolicyStem } from './sweep-eligibility';
import { remainingBalanceValidationSchema, staffChargeValidationSchema } from './manual-charge-form.validation';
import AgentPaymentForm from './agent-payment-form';
import { PaymentResultStatus, manualChargeResult } from '../payment-result-status';
import useStyles from '../payment.styles';

const ManualChargeForm = () => {
  const {
    account: {
      fetchFullAccountAndPolicy,
      policies: { policy: policyInfo }
    }
  } = useStore();
  const {
    user: { username, affinityCode },
    canManuallyChargeFee,
    canManuallyChargePolicy,
    viewOnly,
    isInternalAgent,
    canManageSweepAccount
  } = useSession();
  const classes = useStyles();
  const {
    policy,
    runManualCharge,
    setPaymentResult,
    billingDetails,
    billingHoldBlocksManualPayments,
    totalFeeCost,
    premium,
    surplusContribution,
    totalReceived
  } = policyInfo;

  // Sweep account payments (#33155) are available to an independent agent whose own
  // affinity stem matches the policy's, and only if their agency has a sweep account.
  // The brand/env flag comes first, matching the other call sites (account menu, /sweep-account
  // route, checkout). Without it this query fired wherever sweep is off and the lambda refused it.
  const userStem = affinityCode ? affinityToStem(affinityCode).toUpperCase() : null;
  const policyStem = useMemo(() => resolvePolicyStem(policyInfo, policy), [policyInfo, policy]);
  const sweepEligible =
    isSweepAccountsEnabled() && !isInternalAgent && !!userStem && !!policyStem && userStem === policyStem;
  const { data: sweepData, loading: agencySweepLoading } = useQuery(GET_SWEEP_ACCOUNT, {
    skip: !sweepEligible,
    fetchPolicy: 'no-cache'
  });
  // Agent payment options (#36683): agents without the manual-charge permission pay a preset, and
  // internal staff with it may charge the policy's own agency sweep account when an agent asks.
  const agentPaymentOptions = isAgentPaymentOptionsEnabled();
  const staffSweepEligible =
    agentPaymentOptions && isSweepAccountsEnabled() && isInternalAgent && canManuallyChargePolicy && !!policyStem;
  const { data: policySweepData, loading: policySweepLoading } = useQuery(GET_POLICY_SWEEP_ACCOUNT, {
    variables: { policyId: policy.id },
    skip: !staffSweepEligible,
    fetchPolicy: 'no-cache'
  });
  const sweepLoading = agencySweepLoading || policySweepLoading;
  const sweepAccount = sweepData?.getSweepAccount || policySweepData?.getSweepAccount || null;

  const { allPaymentMethods } = billingDetails;
  const memberPaymentMethodOptions = allPaymentMethods
    ? allPaymentMethods.map((paymentMethod) => {
        const { id, brand, last4, bankName } = paymentMethod;
        const prefix = bankName?.toUpperCase() || brand?.toUpperCase();
        return { id, value: prefix ? `${prefix} ****${last4}` : `****${last4}` };
      })
    : [];

  // Sweep payment methods share the member dropdown, prefixed so staff can tell them apart.
  // Selecting one flips the charge to the agency's sweep account on submit.
  const sweepPaymentMethodIds = new Set((sweepAccount?.paymentMethods ?? []).map((pm) => pm.id));
  const sweepPaymentMethodOptions = (sweepAccount?.paymentMethods ?? []).map((pm) => {
    const prefix = pm.bankName?.toUpperCase() || pm.brand?.toUpperCase();
    return {
      id: pm.id,
      value: `Sweep account — ${prefix ? `${prefix} ` : ''}****${pm.last4}${pm.isDefault ? ' (default)' : ''}`
    };
  });
  const paymentMethodOptions = [...memberPaymentMethodOptions, ...sweepPaymentMethodOptions];

  // Raw method objects (carrying brand vs bankName) keyed by the same id the dropdown uses, so the
  // CC-fee note can be card-gated to the actually-selected method (#35705).
  const selectablePaymentMethods = [...(allPaymentMethods ?? []), ...(sweepAccount?.paymentMethods ?? [])];

  // Shown once a card is selected on a fee-brand farm policy. Reassures the agent that the amount
  // entered is billed in full with no processing fee tacked on — since #35705 a manual charge never
  // adds the CC fee on top (where a fee applies it is already inside the policy's scheduled amount, not
  // a separate second charge). Card-gated: nothing to say when the method isn't a card.
  const creditCardFeeNote = (
    <Grid item>
      <Card className={classes.smallContainer}>
        <Grid className={classes.centeredContentContainer}>
          <img src={icon} className={classes.smallIcon} alt="Notification Icon" />
          <small>
            This payment method is a credit card. The amount entered is charged in full — no additional credit-card
            processing fee is added on top.
          </small>
        </Grid>
      </Card>
    </Grid>
  );
  const selectedMethodFor = (paymentMethodId) =>
    selectablePaymentMethods.find((method) => method.id === paymentMethodId);

  // The sweep account loads via a no-cache query that re-fires on every page open, so its methods
  // land after the initial render. Until it resolves, the dropdown would show only the member
  // methods (or nothing) and appear to "drop" the sweep option — disable + flag the field while
  // loading so it reads as loading rather than empty/broken. (#34093)
  const paymentMethodsLoading = (sweepEligible || staffSweepEligible) && sweepLoading;

  // Independent agent, eligible policy, but no sweep account set up yet → point them to setup.
  // Gated on !sweepLoading so the hint doesn't flash before an existing account resolves, and on
  // CanManageSweepAccount so only someone who can set the account up sees it (matches checkout).
  const sweepSetupHint =
    sweepEligible && canManageSweepAccount && !sweepLoading && !sweepAccount ? (
      <Tooltip
        label="sweep account payments"
        text="Set up a sweep account in the account menu (top-right person icon)."
        className={classes.sweepAccountHint}
      />
    ) : null;

  const [loading, setLoading] = useState(false);
  const onSubmit = useCallback(
    async (values) => {
      setLoading(true);
      setPaymentResult('charge', null);
      const accountId = policy.id.slice(0, 9);
      const isSweepPayment = sweepPaymentMethodIds.has(values.paymentMethod);
      try {
        const res = await runManualCharge(
          policy.id,
          accountId,
          values.paymentMethod,
          values.amount,
          `Manual payment made by ${username}. ${values.internalDescription.trim()}`,
          values.billType,
          undefined, // payAhead
          isSweepPayment, // useSweepAccount
          isSweepPayment ? values.paymentMethod : undefined, // sweepPaymentMethodId
          isSweepPayment ? values.requestedBy?.trim() || undefined : undefined // requestedBy (#36683)
        );
        const charge = res.data?.runManualCharge;
        setPaymentResult('charge', manualChargeResult({ runManualCharge: charge, amount: values.amount }));
        if (charge?.success) {
          fetchFullAccountAndPolicy(policy.id, accountId);
        }
      } catch (error) {
        setPaymentResult('charge', manualChargeResult({ amount: values.amount, error }));
      }
      setLoading(false);
    },
    [runManualCharge, policy.id, setPaymentResult, fetchFullAccountAndPolicy, username, sweepPaymentMethodIds]
  );

  const billTypes = canManuallyChargeFee
    ? getOptions('billTypes')
    : getOptions('billTypes').filter((billType) => billType.id !== 'fee');

  const totalPrice = premium + totalFeeCost + surplusContribution;
  // #37632: the payoff, which leaves out the installment fees of installments that will no longer run.
  const totalRemaining = remainingBalance({ billingDetails, totalPrice, totalReceived });

  if (!viewOnly && !canManuallyChargePolicy && agentPaymentOptions) {
    return (
      <AgentPaymentForm
        paymentMethodOptions={paymentMethodOptions}
        sweepPaymentMethodIds={sweepPaymentMethodIds}
        paymentMethodFieldProps={paymentMethodFieldLoadingProps(paymentMethodsLoading)}
        sweepSetupHint={sweepSetupHint}
        renderCreditCardFeeNote={(paymentMethodId) =>
          creditCardFeeApplies({
            method: selectedMethodFor(paymentMethodId),
            policyType: policy.policyType,
            brandConfig,
            policy
          }) && creditCardFeeNote
        }
      />
    );
  }

  // Remaining balance is set amount to be paid
  // all agents can do a manual charge with exception to viewOnly
  if (!viewOnly && !canManuallyChargePolicy) {
    return (
      <>
        <div className={classes.manualChargeContainer}>
          <Grid container alignItems="center" justifyContent="flex-start" className={classes.rowTitle}>
            <Grid item xs={6}>
              <Label type="coverageValue">Pay remaining balance in full</Label>
            </Grid>
          </Grid>
          {billingHoldBlocksManualPayments && (
            <Grid item>
              <Card className={classes.smallContainer}>
                <Grid className={classes.centeredContentContainer}>
                  <img src={icon} className={classes.smallIcon} alt="Notification Icon" />
                  You must remove the payment hold before making a manual charge.
                </Grid>
              </Card>
            </Grid>
          )}
          <Formik
            initialValues={{
              paymentMethod: '',
              amount: totalRemaining,
              billType: 'Premium + Fee',
              internalDescription: ''
            }}
            validationSchema={remainingBalanceValidationSchema}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={onSubmit}
          >
            {({ handleSubmit, values }) => {
              const showCreditCardFeeNote =
                !billingHoldBlocksManualPayments &&
                creditCardFeeApplies({
                  method: selectedMethodFor(values.paymentMethod),
                  policyType: policy.policyType,
                  brandConfig,
                  policy
                });
              return (
                <div className={classes.manualChargeForm}>
                  {showCreditCardFeeNote && creditCardFeeNote}
                  <Grid container key="row1" spacing={4}>
                    <Grid item xs={6}>
                      <LabelTooltip
                        label="Payment Method"
                        tooltip={{ label: 'More Info', onHoverText: tooltipHoverTexts.paymentTabPaymentMethod }}
                      >
                        <Field
                          name="paymentMethod"
                          type="select"
                          xs={12}
                          mode="dark"
                          options={paymentMethodOptions}
                          {...paymentMethodFieldLoadingProps(paymentMethodsLoading)}
                          ignoreGlobalDisabledState
                        />
                      </LabelTooltip>
                      {sweepSetupHint && <div className={classes.sweepAccountHintRow}>{sweepSetupHint}</div>}
                    </Grid>
                    <Grid container xs={6}>
                      <ValueField
                        label="Pay total remaining balance"
                        name="amount"
                        mode="dark"
                        xs={12}
                        formatter={currencyFormatter}
                        value={totalRemaining}
                        showTooltip={{ label: 'More Info', onHoverText: tooltipHoverTexts.paymentTabRemainingBalance }}
                        className={classes.totalRemainingField}
                        ignoreGlobalDisabledState
                      />
                    </Grid>
                  </Grid>
                  <Grid container key="row2" spacing={4} className={classes.spaceBetweenRow}>
                    <Field
                      name="internalDescription"
                      type="string"
                      label="Description"
                      xs={8}
                      ignoreGlobalDisabledState
                    />
                    <Button
                      variant="contained"
                      color="secondary"
                      className={classes.submit}
                      onClick={handleSubmit}
                      disabled={loading || billingHoldBlocksManualPayments}
                    >
                      Submit Payment
                    </Button>
                  </Grid>
                </div>
              );
            }}
          </Formik>
          <PaymentResultStatus form="charge" />
        </div>
        {loading && <LinearProgress color="secondary" className={classes.loadingBar} />}
      </>
    );
  }

  // Can set amount to be paid
  // Only agents with canManuallyCharge can do a manual charge with the option to set amount to be paid
  return (
    <>
      <div className={classes.manualChargeContainer}>
        <Grid container alignItems="center" justifyContent="flex-start" className={classes.rowTitle}>
          <Grid item xs={3}>
            <Label type="coverageValue">Run a manual charge</Label>
          </Grid>
        </Grid>
        {billingHoldBlocksManualPayments && (
          <Grid item>
            <Card className={classes.smallContainer}>
              <Grid className={classes.centeredContentContainer}>
                <img src={icon} className={classes.smallIcon} alt="Notification Icon" />
                You must remove the payment hold before making a manual charge.
              </Grid>
            </Card>
          </Grid>
        )}
        <Formik
          initialValues={{ paymentMethod: '', amount: null, billType: '', internalDescription: '', requestedBy: '' }}
          // Only staff charging a policy's agency sweep for an agent see (and must fill) Requested by;
          // an agency user's own sweep charge needs no requester.
          validationSchema={staffChargeValidationSchema(staffSweepEligible ? sweepPaymentMethodIds : new Set())}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={onSubmit}
        >
          {({ handleSubmit, values }) => {
            const showCreditCardFeeNote =
              !billingHoldBlocksManualPayments &&
              creditCardFeeApplies({
                method: selectedMethodFor(values.paymentMethod),
                policyType: policy.policyType,
                brandConfig,
                policy
              });
            return (
              <div className={classes.manualChargeForm}>
                {showCreditCardFeeNote && creditCardFeeNote}
                <Grid container key="row1" spacing={4}>
                  <Field
                    name="paymentMethod"
                    type="select"
                    label="Payment Method"
                    xs={4}
                    mode="dark"
                    options={paymentMethodOptions}
                    {...paymentMethodFieldLoadingProps(paymentMethodsLoading)}
                    ignoreGlobalDisabledState
                  />
                  <Field
                    name="amount"
                    type="numeric"
                    label="Amount"
                    xs={4}
                    format={{
                      thousandSeparator: true,
                      prefix: '$',
                      allowNegative: false,
                      fixedDecimalScale: true,
                      decimalScale: 2
                    }}
                    ignoreGlobalDisabledState
                  />
                  <Field
                    name="billType"
                    type="select"
                    label="Bill Type"
                    xs={4}
                    options={billTypes}
                    mode="dark"
                    ignoreGlobalDisabledState
                  />
                </Grid>
                {sweepSetupHint && (
                  <Grid container key="sweep-hint" className={classes.sweepAccountHintRow}>
                    {sweepSetupHint}
                  </Grid>
                )}
                {/* Staff charging the agency sweep account for an agent name that agent (#36683). */}
                {staffSweepEligible && sweepPaymentMethodIds.has(values.paymentMethod) && (
                  <Grid container key="requested-by" spacing={4}>
                    <Field
                      name="requestedBy"
                      type="string"
                      label="Requested by (agent)"
                      xs={8}
                      ignoreGlobalDisabledState
                    />
                  </Grid>
                )}
                <Grid container key="row2" spacing={4} className={classes.spaceBetweenRow}>
                  <Field
                    name="internalDescription"
                    type="string"
                    label="Description"
                    xs={8}
                    ignoreGlobalDisabledState
                  />
                  <Button
                    variant="contained"
                    color="secondary"
                    className={classes.submit}
                    onClick={handleSubmit}
                    disabled={loading || billingHoldBlocksManualPayments}
                  >
                    Charge Member
                  </Button>
                </Grid>
              </div>
            );
          }}
        </Formik>
        <PaymentResultStatus form="charge" />
      </div>
      {loading && <LinearProgress color="secondary" className={classes.loadingBar} />}
    </>
  );
};

export default ManualChargeForm;
